import { useState } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { convertToLegacyLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { getListingUrl } from 'academy-common-lib/features/learningContentCard/routing/RoutingUtils';
import { getPlaylistUrl } from 'academy-common-lib/features/playlist/playlistRouting';
import { useCreatePlaylist } from 'academy-common-lib/features/savedItems/savedItemsQueries';
import UsageTracker from 'academy-common-lib/utils/UsageTracker';
import { useACNavigate } from 'academy-common-lib/hooks/useACNavigate';
import { useStartProgressMutation } from 'academy-common-lib/__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.hooks';
import { jsx as _jsx } from "react/jsx-runtime";
const LISTING_CONTENT_TYPES = ['TRACK', 'LESSON', 'SHORT_FORM_CONTENT'];
const isListingContentType = type => LISTING_CONTENT_TYPES.some(listingType => listingType === type);
const showCreatePlaylistError = () => {
  FloatingAlertStore.addAlert({
    type: 'danger',
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academy-ai-assistant.playlist.startError"
    }),
    timeout: 5000
  });
};
export const useCreateSuggestedPlaylist = ({
  name,
  description,
  items
}) => {
  const navigate = useACNavigate();
  const [startPlaylistProgress] = useStartProgressMutation();
  const [createdPlaylistId, setCreatedPlaylistId] = useState(null);
  const {
    createPlaylist,
    createPlaylistLoading
  } = useCreatePlaylist();
  const firstItem = items[0];
  const openCreatedPlaylist = playlistId => {
    var _firstItem$language;
    if (!firstItem || !isListingContentType(firstItem.type)) {
      navigate(getPlaylistUrl(playlistId));
      return;
    }
    const language = (_firstItem$language = firstItem.language) !== null && _firstItem$language !== void 0 ? _firstItem$language : 'EN';
    startPlaylistProgress({
      variables: {
        contentId: playlistId,
        contentType: 'PLAYLIST',
        language
      }
    }).catch(Raven.captureException.bind(Raven));
    navigate(getListingUrl({
      type: firstItem.type,
      isStarted: false,
      id: firstItem.itemId,
      language: convertToLegacyLanguage(language),
      currentPathname: getPlaylistUrl(playlistId).pathname,
      playlistId
    }));
  };
  const createOrGetPlaylistId = async () => {
    var _fetchResult$data;
    if (createdPlaylistId !== null) {
      return createdPlaylistId;
    }
    const fetchResult = await createPlaylist({
      name,
      description,
      items
    });
    const playlistId = fetchResult === null || fetchResult === void 0 || (_fetchResult$data = fetchResult.data) === null || _fetchResult$data === void 0 || (_fetchResult$data = _fetchResult$data.result) === null || _fetchResult$data === void 0 ? void 0 : _fetchResult$data.id;
    if (typeof playlistId !== 'number') {
      showCreatePlaylistError();
      return null;
    }
    UsageTracker.trackStandalone('Playlist Interaction', {
      action: 'playlist-created',
      location: 'academy-assistant'
    });
    setCreatedPlaylistId(playlistId);
    return playlistId;
  };
  const runWithCreatedPlaylist = async onCreated => {
    if (createPlaylistLoading) return;
    try {
      const playlistId = await createOrGetPlaylistId();
      if (playlistId !== null) {
        onCreated(playlistId);
      }
    } catch (error) {
      showCreatePlaylistError();
      Raven.captureException(error instanceof Error ? error : new Error(String(error)));
    }
  };
  const startPlaylist = () => runWithCreatedPlaylist(openCreatedPlaylist);
  const managePlaylist = () => runWithCreatedPlaylist(playlistId => navigate(getPlaylistUrl(playlistId)));
  return {
    startPlaylist,
    managePlaylist,
    isCreatingPlaylist: createPlaylistLoading
  };
};